/**
 * `MermaidNode` — the React Flow custom node that renders one flowchart tile
 * (interactive-diagrams v0.3).
 *
 * The tile's shape is drawn as a real **SVG background** (`ShapeSvg`, shared geometry — see
 * `shapes.tsx`), so every shape reads unmistakably (diamond, hexagon, parallelogram, cylinder,
 * …) instead of the old CSS boxes that all looked like rectangles. The label sits on top.
 *
 *  - fill / stroke come from the node's native mermaid `style` (portable hexes) or the theme;
 *    selection thickens + recolors the stroke (no rectangular focus ring on non-rect shapes);
 *  - `NodeResizer` when selected + editable — resize persists to the sidecar `size` map;
 *  - handles are connectable (drag from a tile edge to another) and visible on hover/selection;
 *  - double-click the label to rename inline.
 */

import { useEffect, useRef, useState } from 'react';
import { Handle, NodeResizer, Position, type NodeProps, type Node } from '@xyflow/react';
import type { DiagramNode, NodeShape } from '../model';
import { ShapeSvg, INSET_SHAPES } from './shapes';

/** Data payload carried by a mermaid React Flow node. */
export type MermaidNodeData = {
  node: DiagramNode;
  /** Flow direction — decides which sides the source/target handles sit on. */
  direction: string;
  /** Merged native-mermaid style props for this node (`fill`, `stroke`, `color`, `font-size`…). */
  mermaidStyle?: Record<string, string>;
  /** When present (editable canvas), double-clicking the label commits a relabel via this. */
  onRelabel?: (id: string, label: string) => void;
  /** When present (editable canvas), a NodeResizer drag-end commits `[w, h]` via this. */
  onResizeEnd?: (id: string, width: number, height: number) => void;
  [k: string]: unknown;
};

export type MermaidFlowNode = Node<MermaidNodeData, 'mermaid'>;

/** Handle sides for the flow direction: vertical → top/bottom, horizontal → left/right. */
function handleSides(direction: string): { target: Position; source: Position } {
  switch (direction) {
    case 'LR':
      return { target: Position.Left, source: Position.Right };
    case 'RL':
      return { target: Position.Right, source: Position.Left };
    case 'BT':
      return { target: Position.Bottom, source: Position.Top };
    default: // TB / TD
      return { target: Position.Top, source: Position.Bottom };
  }
}

/** Label text CSS from mermaid style props (color / font-size / font-weight only). */
function labelCss(props: Record<string, string> | undefined): React.CSSProperties {
  if (!props) return {};
  const css: React.CSSProperties = {};
  if (props.color) css.color = props.color;
  if (props['font-size']) css.fontSize = props['font-size'];
  if (props['font-weight'])
    css.fontWeight = props['font-weight'] as React.CSSProperties['fontWeight'];
  return css;
}

export function MermaidNode({
  id,
  data,
  selected,
  isConnectable,
}: NodeProps<MermaidFlowNode>): JSX.Element {
  const { node, direction, mermaidStyle, onRelabel, onResizeEnd } = data;
  const shape: NodeShape = node.shape;
  const sides = handleSides(direction);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(node.label);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const startEdit = () => {
    if (!onRelabel) return;
    setDraft(node.label);
    setEditing(true);
  };
  const commit = () => {
    setEditing(false);
    if (onRelabel && draft !== node.label) onRelabel(node.id, draft);
  };

  const fill = mermaidStyle?.fill ?? 'var(--bg-1)';
  const stroke = selected
    ? 'var(--primary)'
    : (mermaidStyle?.stroke ?? 'var(--primary)');
  const strokeWidth = selected ? 2.4 : Number(mermaidStyle?.['stroke-width']) || 1.5;

  return (
    <div
      className={`diagram-node shape-${shape}${selected ? ' is-selected' : ''}${
        INSET_SHAPES.has(shape) ? ' is-inset' : ''
      }`}
      title={`${node.id} · ${shape}`}
    >
      <ShapeSvg shape={shape} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
      {onResizeEnd && (
        <NodeResizer
          isVisible={!!selected}
          minWidth={64}
          minHeight={36}
          lineClassName="diagram-resize-line"
          handleClassName="diagram-resize-handle"
          onResizeEnd={(_e, params) =>
            onResizeEnd(id, Math.round(params.width), Math.round(params.height))
          }
        />
      )}
      <Handle
        type="target"
        position={sides.target}
        className="diagram-node-handle"
        isConnectable={isConnectable}
      />
      {editing ? (
        <input
          ref={inputRef}
          className="diagram-node-input nodrag nopan"
          style={labelCss(mermaidStyle)}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') commit();
            else if (e.key === 'Escape') setEditing(false);
          }}
          onBlur={commit}
          autoFocus
        />
      ) : (
        <span className="diagram-node-label" style={labelCss(mermaidStyle)} onDoubleClick={startEdit}>
          {node.label}
        </span>
      )}
      <Handle
        type="source"
        position={sides.source}
        className="diagram-node-handle"
        isConnectable={isConnectable}
      />
    </div>
  );
}
